import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  CloudOff,
  Circle,
  ClipboardPaste,
  Flag,
  Info,
  Lock,
  MoreHorizontal,
  Plus,
  ShieldAlert,
  ShieldCheck,
  Timer,
  Trash2,
  XCircle,
} from 'lucide-react';
import { ApiError, apiErrorCode, apiFetch, v1Path } from '../../lib/api';
import { isActivityDto, type ActivityDto } from '../../lib/activity-timeline';
import { formatDateOnly, formatTimestampDateTime } from '../../lib/dates';
import { splitAnswers, type NotesAnswer } from '../../lib/notes-fill';
import { isRichTextEmpty } from '../../lib/rich-text';
import { showsDeliveryReadiness } from '../../lib/service-request-views';
import {
  deleteServiceRequest,
  DEPLOYMENT_SERVICE_REQUEST_FIELDS,
  PRIORITY_LABELS,
  SERVICE_REQUEST_CLOSED_STAGES,
  SERVICE_REQUEST_PRIORITIES,
  approvalPhaseForStage,
  approvalRoleRows,
  configuredSections,
  deploymentApprovalLabel,
  detailsDirty,
  detailsPatchBody,
  isDeploymentApproved,
  ragClass,
  ragLabel,
  readinessLabelMap,
  DETAIL_ITEM_PREFIX,
  serviceRequestErrorMessage,
  serviceRequestFieldAllowsOther,
  serviceRequestFieldIsMulti,
  serviceRequestFieldIsQuantityList,
  serviceRequestQuantitySourceOf,
  quantityRowsComplete,
  quantityRowsOf,
  quantityRowsTotal,
  serviceRequestFieldOptions,
  serviceRequestFieldKind,
  serviceRequestOptionColor,
  serviceRequestFieldLabel,
  serviceRequestFieldVisible,
  serviceRequestHandoffs,
  serviceRequestSchemaFieldType,
  serviceRequestPath,
  slaClass,
  slaSummary,
  stageAction,
  stageLabel,
  transitionStateOptions,
  typeDataOf,
  type ApprovalDecisionResponse,
  type ApprovalPhase,
  type ServiceRequest,
  type ServiceRequestApproval,
  type ServiceRequestQuantityRow,
  type ServiceRequestType,
} from '../../lib/service-requests';
import { cn } from '../../lib/utils';
import { isMemberDto, memberDisplayName, parseItems, type MemberDto, type StateDto, type WorkItemFull } from '../../lib/work-types';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Checkbox } from '../ui/checkbox';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';
import ActivityTimeline from './ActivityTimeline';
import DeliveryPanel from './DeliveryPanel';
import FillFromNotesDialog from './FillFromNotesDialog';
import { OwnerPicker } from './InitiativeOwners';
import AttachmentList from './AttachmentList';
import DepartmentSteps, { type ExtraDepartmentStep } from './DepartmentSteps';
import { StageProgress } from './ServiceRequestProgress';
import { useOverlays } from './overlay-context';
import { useProjectDecorations } from './use-project-decorations';

type GateError = { message: string; details: string[] };

function gateErrorFrom(err: unknown, fallback: string): GateError {
  if (err instanceof ApiError) {
    const details = Array.isArray(err.body.error?.details)
      ? err.body.error.details.map((row) => row.message).filter((row): row is string => typeof row === 'string')
      : [];
    const headline = typeof err.body.error?.message === 'string' ? err.body.error.message : '';
    return { message: (details.length && headline) || err.message || fallback, details };
  }
  return { message: err instanceof Error ? err.message : fallback, details: [] };
}

function dateText(value: string | null | undefined): string {
  if (!value) return '—';
  return formatDateOnly(value) ?? value;
}

function timestampText(value: string | null | undefined): string {
  if (!value) return '—';
  return formatTimestampDateTime(value) ?? value;
}

function DetailField({
  field,
  value,
  onChange,
  requestType,
  disabled = false,
  idPrefix = 'detail',
  typeData,
}: {
  field: string;
  value: unknown;
  onChange: (value: unknown) => void;
  requestType?: ServiceRequestType | null;
  disabled?: boolean;
  idPrefix?: string;
  /** The request's other answers, for questions that read a sibling (e.g. a list's legacy count). */
  typeData?: Record<string, unknown>;
}) {
  const kind = serviceRequestFieldKind(field, requestType);
  const options = serviceRequestFieldOptions(requestType, field);
  const configuredType = serviceRequestSchemaFieldType(requestType, field);
  const id = `${idPrefix}-${field}`;
  const label = serviceRequestFieldLabel(field, requestType);
  if (requestType?.field_schema?.must_confirm?.includes(field)) {
    return (
      <label htmlFor={id} className="flex items-start gap-3 rounded-lg border bg-muted/40 px-3 py-2.5 text-sm sm:col-span-2">
        <Checkbox id={id} className="mt-0.5" checked={value === true} disabled={disabled} onCheckedChange={(checked) => onChange(checked === true ? true : null)} />
        <span>{label}</span>
      </label>
    );
  }
  if (kind === 'boolean') {
    return (
      <div className="space-y-1">
        <Label htmlFor={id} className="text-xs text-muted-foreground">{label}</Label>
        <Select
          value={typeof value === 'boolean' ? String(value) : '__unanswered__'}
          disabled={disabled}
          onValueChange={(next) => onChange(next === '__unanswered__' ? null : next === 'true')}
        >
          <SelectTrigger id={id}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__unanswered__">Not answered</SelectItem>
            <SelectItem value="true">Yes</SelectItem>
            <SelectItem value="false">No</SelectItem>
          </SelectContent>
        </Select>
      </div>
    );
  }
  if (options.length && serviceRequestFieldIsQuantityList(requestType, field)) {
    const totalKey = requestType?.field_schema?.quantity_total?.[field];
    return (
      <QuantityListField
        id={id}
        label={label}
        options={options}
        allowOther={serviceRequestFieldAllowsOther(requestType, field)}
        rows={quantityRowsOf(value, totalKey ? typeData?.[totalKey] : undefined)}
        totalLabel={totalKey ? serviceRequestFieldLabel(totalKey, requestType) : null}
        disabled={disabled}
        onChange={(rows) => onChange(rows.length ? rows : null)}
      />
    );
  }
  const quantitySource = serviceRequestQuantitySourceOf(requestType, field);
  if (quantitySource) {
    return (
      <div className="space-y-1">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="flex h-9 items-center rounded-md border border-dashed bg-muted/40 px-3 text-sm tabular-nums">
          {typeof value === 'number' ? value : '—'}
          <span className="ml-2 text-xs text-muted-foreground">Added up from {serviceRequestFieldLabel(quantitySource, requestType)}</span>
        </p>
      </div>
    );
  }
  if (options.length && serviceRequestFieldIsMulti(requestType, field)) {
    return (
      <MultiChoiceField
        id={id}
        label={label}
        options={options}
        allowOther={serviceRequestFieldAllowsOther(requestType, field)}
        value={value}
        disabled={disabled}
        onChange={onChange}
      />
    );
  }
  if (options.length && serviceRequestFieldAllowsOther(requestType, field)) {
    return <ChoiceWithOtherField id={id} label={label} options={options} value={value} disabled={disabled} onChange={onChange} />;
  }
  if (options.length) {
    return (
      <div className="space-y-1">
        <Label htmlFor={id} className="text-xs text-muted-foreground">{label}</Label>
        <Select
          value={typeof value === 'string' && value ? value : '__unanswered__'}
          disabled={disabled}
          onValueChange={(next) => onChange(next === '__unanswered__' ? null : next)}
        >
          <SelectTrigger id={id}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__unanswered__">Not answered</SelectItem>
            {options.map((option) => (
              <SelectItem key={option} value={option}>
                <OptionLabel option={option} color={serviceRequestOptionColor(requestType, field, option)} />
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }
  const text = Array.isArray(value) ? value.join(', ') : value == null ? '' : String(value);
  return (
    <div className="space-y-1">
      <Label htmlFor={id} className="text-xs text-muted-foreground">{label}</Label>
      {kind === 'textarea' ? (
        <Textarea id={id} value={text} disabled={disabled} onChange={(event) => onChange(event.target.value)} />
      ) : (
        <Input
          id={id}
          type={kind}
          min={configuredType === 'positive_integer' ? 1 : undefined}
          step={configuredType === 'positive_integer' ? 1 : undefined}
          value={text}
          disabled={disabled}
          onChange={(event) => onChange(
            kind === 'number'
              ? (event.target.value === '' ? null : Number(event.target.value))
              : event.target.value,
          )}
        />
      )}
    </div>
  );
}

const OPTION_DOT: Record<string, string> = {
  red: 'bg-red-500',
  amber: 'bg-amber-500',
  green: 'bg-emerald-500',
};

function OptionLabel({ option, color }: { option: string; color: string | null }) {
  if (!color) return <>{option}</>;
  return (
    <span className="inline-flex items-center gap-2">
      <span className={cn('h-2.5 w-2.5 shrink-0 rounded-full', OPTION_DOT[color])} aria-hidden />
      {option}
    </span>
  );
}

const OTHER_CHOICE = '__other__';

type ChoiceFieldProps = {
  id: string;
  label: string;
  options: string[];
  value: unknown;
  disabled: boolean;
  onChange: (value: unknown) => void;
};

/** Pick one listed answer, or "Other" and type it. */
function ChoiceWithOtherField({ id, label, options, value, disabled, onChange }: ChoiceFieldProps) {
  const text = typeof value === 'string' ? value : '';
  const [otherOpen, setOtherOpen] = useState(() => Boolean(text) && !options.includes(text));
  const showOther = otherOpen || (Boolean(text) && !options.includes(text));
  return (
    <div className="space-y-1">
      <Label htmlFor={id} className="text-xs text-muted-foreground">{label}</Label>
      <Select
        value={showOther ? OTHER_CHOICE : text || '__unanswered__'}
        disabled={disabled}
        onValueChange={(next) => {
          if (next === OTHER_CHOICE) {
            setOtherOpen(true);
            onChange(null);
            return;
          }
          setOtherOpen(false);
          onChange(next === '__unanswered__' ? null : next);
        }}
      >
        <SelectTrigger id={id}><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="__unanswered__">Not answered</SelectItem>
          {options.map((option) => <SelectItem key={option} value={option}>{option}</SelectItem>)}
          <SelectItem value={OTHER_CHOICE}>Other…</SelectItem>
        </SelectContent>
      </Select>
      {showOther ? (
        <Input
          aria-label={`${label}: other`}
          placeholder="Type the answer"
          autoFocus={otherOpen && !text}
          value={text}
          disabled={disabled}
          onChange={(event) => onChange(event.target.value)}
        />
      ) : null}
    </div>
  );
}

/** One row per choice with how many, e.g. Blinx × 5, Europa × 3; the total is added up for you. */
function QuantityListField({
  id,
  label,
  options,
  allowOther,
  rows,
  totalLabel,
  disabled,
  onChange,
}: {
  id: string;
  label: string;
  options: string[];
  allowOther: boolean;
  rows: ServiceRequestQuantityRow[];
  totalLabel: string | null;
  disabled: boolean;
  onChange: (rows: ServiceRequestQuantityRow[]) => void;
}) {
  const [otherRows, setOtherRows] = useState<Set<number>>(() => new Set());
  const update = (index: number, patch: Partial<ServiceRequestQuantityRow>) =>
    onChange(rows.map((row, at) => (at === index ? { ...row, ...patch } : row)));
  const remove = (index: number) => {
    setOtherRows((current) => new Set(Array.from(current).filter((at) => at !== index).map((at) => (at > index ? at - 1 : at))));
    onChange(rows.filter((_, at) => at !== index));
  };
  const lower = (text: string) => text.trim().toLocaleLowerCase();
  const total = quantityRowsTotal(rows);
  return (
    <fieldset className="space-y-2 sm:col-span-2" id={id}>
      <legend className="text-xs text-muted-foreground">{label} <span className="text-muted-foreground/70">· add a row per model</span></legend>
      {rows.length ? (
        <div className="space-y-2 pt-1">
          {rows.map((row, index) => {
            const typed = otherRows.has(index) || (Boolean(row.option) && !options.includes(row.option));
            const duplicate = Boolean(row.option.trim())
              && rows.some((other, at) => at !== index && lower(other.option) === lower(row.option));
            const taken = new Set(rows.filter((_, at) => at !== index).map((other) => other.option));
            return (
              <div key={index} className="flex flex-wrap items-start gap-2">
                <div className="min-w-[12rem] flex-1 space-y-1.5">
                  <Select
                    value={typed ? OTHER_CHOICE : row.option || '__unanswered__'}
                    disabled={disabled}
                    onValueChange={(next) => {
                      if (next === OTHER_CHOICE) {
                        setOtherRows((current) => new Set(current).add(index));
                        update(index, { option: '' });
                        return;
                      }
                      setOtherRows((current) => { const copy = new Set(current); copy.delete(index); return copy; });
                      update(index, { option: next === '__unanswered__' ? '' : next });
                    }}
                  >
                    <SelectTrigger aria-label={`${label} row ${index + 1}: model`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__unanswered__">Choose…</SelectItem>
                      {options.filter((option) => option === row.option || !taken.has(option)).map((option) => (
                        <SelectItem key={option} value={option}>{option}</SelectItem>
                      ))}
                      {allowOther ? <SelectItem value={OTHER_CHOICE}>Other…</SelectItem> : null}
                    </SelectContent>
                  </Select>
                  {typed ? (
                    <Input
                      aria-label={`${label} row ${index + 1}: other model`}
                      placeholder="Type the model"
                      autoFocus={!row.option}
                      value={row.option}
                      disabled={disabled}
                      onChange={(event) => update(index, { option: event.target.value })}
                    />
                  ) : null}
                  {duplicate ? <p className="text-xs text-destructive">{row.option.trim()} is already listed.</p> : null}
                </div>
                <Input
                  type="number"
                  min={1}
                  step={1}
                  inputMode="numeric"
                  aria-label={`${label} row ${index + 1}: quantity`}
                  placeholder="Qty"
                  className="w-24"
                  value={row.quantity ?? ''}
                  disabled={disabled}
                  onChange={(event) => {
                    const parsed = Number(event.target.value);
                    update(index, { quantity: event.target.value === '' || !Number.isInteger(parsed) || parsed < 1 ? null : parsed });
                  }}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label={`Remove ${label} row ${index + 1}`}
                  disabled={disabled}
                  onClick={() => remove(index)}
                >
                  <Trash2 className="h-4 w-4" aria-hidden />
                </Button>
              </div>
            );
          })}
        </div>
      ) : null}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled || rows.length >= 50}
          onClick={() => onChange([...rows, { option: '', quantity: null }])}
        >
          <Plus className="mr-1 h-3.5 w-3.5" aria-hidden />
          Add model
        </Button>
        {rows.length ? (
          <span className="text-sm tabular-nums text-muted-foreground">
            {totalLabel ?? 'Total'}: <span className="font-medium text-foreground">{total}</span>
          </span>
        ) : null}
      </div>
    </fieldset>
  );
}

/** Tick every answer that applies. A typed answer is kept when the question allows Other. */
function MultiChoiceField({ id, label, options, value, disabled, onChange, allowOther = false }: ChoiceFieldProps & { allowOther?: boolean }) {
  const listed = Array.isArray(value) ? value : typeof value === 'string' && value ? [value] : [];
  const selected = listed.filter((entry): entry is string => typeof entry === 'string' && entry.trim().length > 0);
  const choices = allowOther ? options.filter((option) => option.trim().toLocaleLowerCase() !== 'other') : options;
  const custom = selected.filter((entry) => !choices.includes(entry));
  const [typed, setTyped] = useState('');
  const commit = (next: string[]) => onChange(next.length ? next : null);
  const toggle = (option: string) => {
    const next = selected.includes(option) ? selected.filter((entry) => entry !== option) : [...selected, option];
    commit(next);
  };
  const addTyped = () => {
    const text = typed.trim();
    if (!text || selected.some((entry) => entry.toLocaleLowerCase() === text.toLocaleLowerCase())) {
      setTyped('');
      return;
    }
    commit([...selected, text]);
    setTyped('');
  };
  return (
    <fieldset className="space-y-1.5 sm:col-span-2" id={id}>
      <legend className="text-xs text-muted-foreground">{label} <span className="text-muted-foreground/70">· select all that apply</span></legend>
      <div className="flex flex-wrap gap-2 pt-1">
        {choices.map((option) => {
          const on = selected.includes(option);
          return (
            <button
              key={option}
              type="button"
              role="checkbox"
              aria-checked={on}
              disabled={disabled}
              onClick={() => toggle(option)}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm transition-colors disabled:opacity-60',
                on ? 'border-primary bg-primary/10 text-foreground' : 'border-input bg-card text-muted-foreground hover:bg-muted',
              )}
            >
              {on ? <CheckCircle2 className="h-3.5 w-3.5 text-primary" aria-hidden /> : <Circle className="h-3.5 w-3.5" aria-hidden />}
              {option}
            </button>
          );
        })}
        {custom.map((option) => (
          <button
            key={option}
            type="button"
            disabled={disabled}
            onClick={() => toggle(option)}
            className="inline-flex items-center gap-1.5 rounded-full border border-primary bg-primary/10 px-3 py-1 text-sm"
          >
            <CheckCircle2 className="h-3.5 w-3.5 text-primary" aria-hidden />
            {option}
          </button>
        ))}
      </div>
      {allowOther ? (
        <div className="flex max-w-sm gap-2 pt-1">
          <Input
            aria-label={`${label}: other`}
            placeholder="Other…"
            value={typed}
            disabled={disabled}
            onChange={(event) => setTyped(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                addTyped();
              }
            }}
          />
          <Button type="button" variant="outline" size="sm" disabled={disabled || !typed.trim()} onClick={addTyped}>Add</Button>
        </div>
      ) : null}
    </fieldset>
  );
}

function FieldSection({
  title,
  description,
  fields,
  values,
  onChange,
  requestType,
  disabled = false,
}: {
  title: string;
  description?: string;
  fields: readonly string[];
  values: Record<string, unknown>;
  onChange: (field: string, value: unknown) => void;
  requestType?: ServiceRequestType | null;
  disabled?: boolean;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{title}</CardTitle>
        {description ? <CardDescription>{description}</CardDescription> : null}
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          {fields.map((field) => <DetailField key={field} field={field} value={values[field]} requestType={requestType} disabled={disabled} onChange={(value) => onChange(field, value)} />)}
        </div>
      </CardContent>
    </Card>
  );
}

/** A configured requirements section: collapsible, with how much of it is answered. */
function ConfiguredSectionCard({
  id,
  index,
  label,
  answered,
  total,
  open,
  onToggle,
  byline,
  review,
  feedback,
  children,
}: {
  id: string;
  index: number;
  label: string;
  answered: number;
  total: number;
  open: boolean;
  onToggle: () => void;
  /** Who has answered this section and when it last changed. */
  byline?: string | null;
  /** Reviewer mode: flag this section with a note for the requestor. */
  review?: { flagged: boolean; note: string; onFlag: (flagged: boolean) => void; onNote: (note: string) => void } | null;
  /** The reviewer's note from the last Request changes. */
  feedback?: { note: string; from: string; label: string } | null;
  children: ReactNode;
}) {
  const complete = total > 0 && answered >= total;
  const attention = Boolean(review?.flagged || feedback);
  return (
    <Card id={id} className={cn('scroll-mt-4', attention && 'border-amber-300 dark:border-amber-800')}>
      <div className="flex items-center gap-2 pr-3">
        <button
          type="button"
          aria-expanded={open}
          onClick={onToggle}
          className="flex min-w-0 flex-1 items-center gap-3 px-5 py-4 text-left"
        >
          {feedback
            ? <Flag className="h-6 w-6 shrink-0 text-amber-500" aria-hidden />
            : complete
              ? <CheckCircle2 className="h-6 w-6 shrink-0 text-emerald-500" aria-hidden />
              : <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">{index + 1}</span>}
          <span className="min-w-0 flex-1">
            <span className="block text-base font-semibold">{label}</span>
            {byline ? <span className="block truncate text-xs font-normal text-muted-foreground">{byline}</span> : null}
          </span>
          {feedback
            ? <span className="text-xs font-medium text-amber-700 dark:text-amber-300">{feedback.label}</span>
            : <span className={cn('text-xs tabular-nums', complete ? 'text-emerald-600' : 'text-muted-foreground')}>{total ? `${answered} of ${total} required` : 'Optional'}</span>}
          <ChevronDown className={cn('h-4 w-4 shrink-0 text-muted-foreground transition-transform', open && 'rotate-180')} aria-hidden />
        </button>
        {review ? (
          <Button
            type="button"
            size="sm"
            variant={review.flagged ? 'secondary' : 'ghost'}
            className={cn('h-8 gap-1.5', review.flagged && 'bg-amber-100 text-amber-800 hover:bg-amber-200 dark:bg-amber-900 dark:text-amber-100')}
            aria-pressed={review.flagged}
            onClick={() => review.onFlag(!review.flagged)}
          >
            <Flag className="h-3.5 w-3.5" aria-hidden />
            {review.flagged ? 'Flagged' : 'Flag'}
          </Button>
        ) : null}
      </div>
      {review?.flagged ? (
        <div className="border-t border-amber-200 bg-amber-50 px-5 py-3 dark:border-amber-900 dark:bg-amber-950">
          <Textarea
            autoFocus
            aria-label={`What needs changing in ${label}`}
            value={review.note}
            placeholder="What needs changing in this section?"
            className="min-h-16 bg-white dark:bg-background"
            onChange={(event) => review.onNote(event.target.value)}
          />
        </div>
      ) : null}
      {feedback ? (
        <div className="border-t border-amber-200 bg-amber-50 px-5 py-3 text-sm text-amber-950 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-100">
          <p className="text-xs font-semibold">{feedback.from}</p>
          <p className="mt-0.5 whitespace-pre-wrap">{feedback.note}</p>
        </div>
      ) : null}
      {open ? <CardContent className="grid gap-4 border-t pt-4 sm:grid-cols-2">{children}</CardContent> : null}
    </Card>
  );
}

const NONE_VALUE = '__none__';

function MemberPicker({
  id,
  label,
  value,
  members,
  onChange,
  disabled,
  fallbackName,
  hint,
}: {
  id: string;
  label: string;
  value: string | null | undefined;
  members: MemberDto[];
  onChange: (userId: string | null) => void;
  disabled: boolean;
  fallbackName?: string | null;
  hint?: string;
}) {
  const known = members.some((member) => member.user_id === value);
  return (
    <div className="space-y-1">
      <Label htmlFor={id} className="text-xs text-muted-foreground">{label}</Label>
      <Select value={value ?? NONE_VALUE} onValueChange={(next) => onChange(next === NONE_VALUE ? null : next)} disabled={disabled}>
        <SelectTrigger id={id}><SelectValue placeholder="Not set" /></SelectTrigger>
        <SelectContent>
          <SelectItem value={NONE_VALUE}>Not set</SelectItem>
          {value && !known ? <SelectItem value={value}>{fallbackName || 'Unknown member'}</SelectItem> : null}
          {members.map((member) => <SelectItem key={member.user_id} value={member.user_id}>{memberDisplayName(member)}</SelectItem>)}
        </SelectContent>
      </Select>
      {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
    </div>
  );
}

/** One cell of the header summary strip: a quiet label above the value that matters. */
function MetaCell({ label, children, className }: { label: string; children: ReactNode; className?: string }) {
  return (
    <div className={cn('bg-background px-3 py-2', className)}>
      <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
      <div className="mt-1 min-w-0 text-sm">{children}</div>
    </div>
  );
}

export default function ServiceRequestDetail({
  projectId,
  request,
  states,
  onChanged,
  onDeleted,
  chrome = 'page',
}: {
  projectId: string;
  request: ServiceRequest;
  states: StateDto[];
  onChanged: (request: ServiceRequest) => void;
  /** Called after a delete; without it the page goes back to the Service requests list. */
  onDeleted?: () => void;
  chrome?: 'page' | 'peek';
}) {
  const { toast, confirm } = useOverlays();
  const navigate = useNavigate();
  const { members, labels } = useProjectDecorations(projectId);
  const [orgMembers, setOrgMembers] = useState<MemberDto[]>([]);
  const [draft, setDraft] = useState<Record<string, unknown>>(request.details);
  const [activity, setActivity] = useState<ActivityDto[]>([]);
  const [saving, setSaving] = useState(false);
  const [transitioning, setTransitioning] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [decidingRole, setDecidingRole] = useState<string | null>(null);
  const [commentFor, setCommentFor] = useState<string | null>(null);
  const [approvalComments, setApprovalComments] = useState('');
  const [evidenceFor, setEvidenceFor] = useState<string | null>(null);
  const [evidenceText, setEvidenceText] = useState('');
  const [validationEvidenceFor, setValidationEvidenceFor] = useState<string | null>(null);
  const [validationEvidenceText, setValidationEvidenceText] = useState('');
  const [gateError, setGateError] = useState<GateError | null>(null);
  const [ragOpen, setRagOpen] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);

  const typeData = useMemo(() => typeDataOf(draft), [draft]);
  const configured = useMemo(() => configuredSections(request.request_type), [request.request_type]);
  const dirty = useMemo(() => detailsDirty(request.details, draft), [request.details, draft]);

  const stage = request.stage;
  const closedStage = stage === 'completed' || stage === 'rejected' || stage === 'cancelled';
  const action = stageAction(stage);
  const deploymentApproved = isDeploymentApproved(request.deployment_approval);
  const preDeployment = stage === 'ready_for_deployment' || stage === 'deployment_approval';
  const permissions = request.permissions;
  const readOnly = Boolean(request.work_item.archived_at) || permissions?.can_edit === false;
  const isOrgAdmin = Boolean(permissions?.is_org_admin);
  const canEditReadiness = !readOnly && (permissions?.can_edit_readiness ?? permissions?.can_edit ?? true);
  const canEditValidation = !readOnly && (permissions?.can_edit_validation ?? permissions?.can_edit ?? true);
  const canEditRequestor = !readOnly && Boolean(permissions?.can_edit_requestor ?? isOrgAdmin);
  const approvalPhase: ApprovalPhase | null = approvalPhaseForStage(stage, {
    initial: request.initial_approval?.status,
    deployment: request.deployment_approval?.status,
  });
  const roleRows = useMemo(
    () => (approvalPhase ? approvalRoleRows(
      request,
      approvalPhase,
      request.details.request_version,
      approvalPhase === 'initial' ? request.initial_approval.current_role : request.deployment_approval.current_role,
    ) : []),
    [request, approvalPhase],
  );
  const requiredItems = useMemo(() => new Set([
    ...(request.request_type.submission_requirements?.type_data_fields ?? []),
    ...(request.request_type.submission_requirements?.fields ?? []).map((field) => `${DETAIL_ITEM_PREFIX}${field}`),
  ]), [request.request_type]);
  const sectionProgress = configured.map((section) => {
    const visible = section.items.filter((item) => item.startsWith(DETAIL_ITEM_PREFIX) || serviceRequestFieldVisible(request.request_type, item, typeData));
    const required = visible.filter((item) => requiredItems.has(item));
    return { ...section, visible, required: required.length, answered: required.filter(itemAnswered).length };
  });
  const sectionsLeft = sectionProgress.filter((section) => section.answered < section.required).length;
  /** Section key → who answered it and when, from the per-answer audit trail. */
  const sectionAuthors = useMemo(() => {
    const sectionOfField = new Map<string, string>();
    for (const section of configured) {
      for (const item of section.items) {
        sectionOfField.set(
          item.startsWith(DETAIL_ITEM_PREFIX)
            ? `service_request.${item.slice(DETAIL_ITEM_PREFIX.length)}`
            : `service_request.type_data.${item}`,
          section.key,
        );
      }
    }
    const result: Record<string, { people: string[]; last: string }> = {};
    for (const row of activity) {
      const sectionKey = row.field ? sectionOfField.get(row.field) : undefined;
      if (!sectionKey || !row.actor_id) continue;
      const entry = result[sectionKey] ?? { people: [], last: row.created_at };
      if (!entry.people.includes(row.actor_id)) entry.people.push(row.actor_id);
      if (row.created_at > entry.last) entry.last = row.created_at;
      result[sectionKey] = entry;
    }
    return result;
  }, [activity, configured]);
  const simpleDraft = stage === 'draft' || stage === 'changes_required';
  /** Drafts save as you go; after submission a saved change is a new revision, so it stays explicit. */
  const autoSave = simpleDraft && !readOnly;
  const [autoSaveState, setAutoSaveState] = useState<'idle' | 'saved' | 'error'>('idle');
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const versionRef = useRef(request.details.request_version);
  versionRef.current = request.details.request_version;
  /** Set when edits landed while an autosave was in flight, so the server copy must not overwrite them. */
  const keepLocalDraft = useRef(false);
  const failedDraft = useRef<Record<string, unknown> | null>(null);
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({});
  /** Reviewer's flagged sections (key → note) for the next Request changes. */
  const [sectionFlags, setSectionFlags] = useState<Record<string, string>>({});
  /** The open Request changes: still each role's latest decision at this revision. */
  const returnedFeedback = (['changes_required', 'submitted', 'under_review'] as string[]).includes(stage)
    ? (() => {
      const latestByRole = new Map<string, ServiceRequestApproval>();
      for (const approval of request.approvals
        .filter((row) => row.phase === 'initial' && row.decision !== 'commented' && row.request_version === request.details.request_version)
        .sort((a, b) => a.decided_at.localeCompare(b.decided_at))) {
        latestByRole.set(approval.role, approval);
      }
      return Array.from(latestByRole.values())
        .filter((row) => row.decision === 'changes_requested')
        .sort((a, b) => b.decided_at.localeCompare(a.decided_at))[0] ?? null;
    })()
    : null;
  const feedbackBySection = returnedFeedback?.section_feedback ?? {};
  /** Open/closed as first shown, so answering the last field never collapses the section under the user. */
  const initialOpen = useRef<Record<string, boolean>>({});
  const sectionOpen = (key: string, complete: boolean) => {
    if (openSections[key] !== undefined) return openSections[key];
    if (reviewing) return true;
    initialOpen.current[key] ??= !complete;
    return initialOpen.current[key] || Boolean(feedbackBySection[key]);
  };
  function goToSection(key: string) {
    setOpenSections((current) => ({ ...current, [key]: true }));
    requestAnimationFrame(() => document.getElementById(`sr-section-${key}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
  }
  const submitReason = !action?.submits
    ? null
    : sectionsLeft
      ? `Finish ${sectionsLeft} section${sectionsLeft === 1 ? '' : 's'} to submit.`
      : dirty ? (autoSave ? 'Saving your changes…' : 'Save your changes before submitting.') : null;
  const validationConfirmedAt = typeof request.details.validation_confirmed_at === 'string' ? request.details.validation_confirmed_at : null;
  const deploymentDate = typeof request.details.deployment_date === 'string' ? request.details.deployment_date : null;
  const memberOptions = orgMembers.length ? orgMembers : members;
  const pushedTaskIds = Array.isArray(request.details.generated_task_ids)
    ? request.details.generated_task_ids.filter((id): id is string => typeof id === 'string' && id.length > 0)
    : [];
  const handoffs = serviceRequestHandoffs(request.request_type);
  // Types that route sections to departments can start before approval. A type with only
  // department checklists (Event Activation) waits for approval, so there is nothing to push.
  const routesBeforeApproval = Object.keys(request.request_type.field_schema?.section_routes ?? {}).length > 0;
  const canPush = !readOnly
    && routesBeforeApproval
    && pushedTaskIds.length === 0
    && handoffs.length > 0
    && !(SERVICE_REQUEST_CLOSED_STAGES as readonly string[]).includes(stage);
  const activityOptions = useMemo(() => ({
    serviceRequest: {
      readinessLabels: readinessLabelMap(request.readiness.items),
      validationLabels: readinessLabelMap(request.validation.items),
      requestType: request.request_type,
    },
  }), [request.readiness.items, request.validation.items, request.request_type]);

  useEffect(() => {
    if (keepLocalDraft.current) {
      keepLocalDraft.current = false;
      return;
    }
    setDraft(request.details);
  }, [request.details]);
  useEffect(() => {
    void apiFetch<unknown>(v1Path(`/projects/${projectId}/work-items/${request.work_item.id}/activity`))
      .then((result) => setActivity(parseItems(result, isActivityDto, 'activity')))
      .catch(() => setActivity([]));
  }, [projectId, request.work_item.id, request]);
  useEffect(() => {
    let cancelled = false;
    void apiFetch<unknown>(v1Path('/members?limit=100&offset=0'))
      .then((result) => {
        if (!cancelled) setOrgMembers(parseItems(result, isMemberDto, 'organization members').filter((member) => member.is_active));
      })
      .catch(() => {
        if (!cancelled) setOrgMembers([]);
      });
    return () => { cancelled = true; };
  }, []);
  const replace = useCallback((next: ServiceRequest) => {
    onChanged(next);
    setDraft(next.details);
    window.dispatchEvent(new CustomEvent('bb:work-items-changed'));
  }, [onChanged]);

  const refetch = useCallback(async () => {
    const next = await apiFetch<ServiceRequest>(v1Path(`/projects/${projectId}/service-requests/${request.work_item.id}`));
    replace(next);
    return next;
  }, [projectId, request.work_item.id, replace]);

  const personName = useCallback((userId: string | null | undefined, fallback = 'Unknown'): string => {
    if (!userId) return fallback;
    const member = members.find((row) => row.user_id === userId) ?? orgMembers.find((row) => row.user_id === userId);
    if (member) return memberDisplayName(member);
    const refs = [request.owner, request.requestor, request.account_owner, request.commercial_owner, ...(request.assignees ?? [])];
    const ref = refs.find((row) => row?.user_id === userId);
    return ref?.name || fallback;
  }, [members, orgMembers, request.owner, request.requestor, request.account_owner, request.commercial_owner, request.assignees]);

  function toastError(err: unknown, fallback: string) {
    const code = apiErrorCode(err);
    toast(serviceRequestErrorMessage(code, err instanceof Error ? err.message : fallback));
  }

  async function saveExtraSteps(steps: ExtraDepartmentStep[]) {
    const snapshot = { ...draftRef.current, extra_steps: steps };
    draftRef.current = snapshot;
    setDraft(snapshot);
    await saveDetails(false);
  }

  async function saveDetails(auto = false): Promise<ServiceRequest | null> {
    const snapshot = draftRef.current;
    setSaving(true);
    try {
      const body = detailsPatchBody(snapshot, { includeRequestor: canEditRequestor });
      const next = await apiFetch<ServiceRequest>(
        v1Path(`/projects/${projectId}/service-requests/${request.work_item.id}/details`),
        { method: 'PATCH', body: JSON.stringify({ ...body, request_version: versionRef.current }) },
      );
      failedDraft.current = null;
      if (auto) {
        const editedSince = draftRef.current !== snapshot;
        keepLocalDraft.current = editedSince;
        onChanged(next);
        if (!editedSince) setDraft(next.details);
        window.dispatchEvent(new CustomEvent('bb:work-items-changed'));
        setAutoSaveState('saved');
      } else {
        replace(next);
        toast('Service Request details saved');
      }
      return next;
    } catch (err) {
      if (auto) {
        failedDraft.current = snapshot;
        if (autoSaveState !== 'error') toastError(err, 'Failed to save details');
        setAutoSaveState('error');
      } else {
        toastError(err, 'Failed to save details');
      }
      return null;
    } finally {
      setSaving(false);
    }
  }
  const saveRef = useRef(saveDetails);
  saveRef.current = saveDetails;
  const autoSavePending = useRef(false);
  autoSavePending.current = autoSave && dirty && !saving;

  useEffect(() => {
    if (!autoSave || !dirty || saving || failedDraft.current === draft) return undefined;
    const timer = window.setTimeout(() => void saveRef.current(true), 800);
    return () => window.clearTimeout(timer);
  }, [autoSave, dirty, saving, draft]);
  useEffect(() => () => {
    if (autoSavePending.current) void saveRef.current(true);
  }, []);

  async function transitionTo(stateId: string, version = request.work_item.version) {
    setGateError(null);
    setTransitioning(true);
    try {
      const item = await apiFetch<WorkItemFull>(
        v1Path(`/projects/${projectId}/work-items/${request.work_item.id}`),
        { method: 'PATCH', body: JSON.stringify({ version, state_id: stateId }) },
      );
      const next = await apiFetch<ServiceRequest>(v1Path(`/projects/${projectId}/service-requests/${item.id}`));
      replace(next);
      toast(`Moved to ${next.state_name ?? stageLabel(next.stage)}`);
    } catch (err) {
      const gate = gateErrorFrom(err, 'State transition blocked');
      gate.message = serviceRequestErrorMessage(apiErrorCode(err), gate.message);
      setGateError(gate);
      toast(gate.message);
    } finally {
      setTransitioning(false);
    }
  }

  function stateForStage(target: string): StateDto | null {
    return states.find((state) => state.service_request_stage === target) ?? null;
  }

  async function runStageAction() {
    if (!action) return;
    const target = stateForStage(action.target);
    if (!target) {
      setGateError({ message: `The Service Request workflow is not configured for "${stageLabel(action.target)}".`, details: [] });
      return;
    }
    let version = request.work_item.version;
    let requestVersion = request.details.request_version;
    if (dirty && !readOnly) {
      const saved = await saveDetails(true);
      if (!saved) return;
      version = saved.work_item.version;
      requestVersion = saved.details.request_version;
    }
    if (action.confirmsValidation && request.validation.items.length === 0) {
      setGateError(null);
      setTransitioning(true);
      try {
        const confirmed = await apiFetch<ServiceRequest>(
          v1Path(`/projects/${projectId}/service-requests/${request.work_item.id}/details`),
          { method: 'PATCH', body: JSON.stringify({ validation_confirmed: true, request_version: requestVersion }) },
        );
        replace(confirmed);
        await transitionTo(target.id, confirmed.work_item.version);
      } catch (err) {
        const gate = gateErrorFrom(err, 'Failed to confirm validation');
        setGateError(gate);
        toast(gate.message);
        setTransitioning(false);
      }
      return;
    }
    await transitionTo(target.id, version);
  }

  async function pushIntoProjects() {
    const ok = await confirm({
      title: 'Push into projects?',
      description: 'This creates the project tasks from what is filled in so far. The request stays where it is, and the remaining answers can be added later.',
      confirmLabel: 'Push into projects',
    });
    if (!ok) return;
    setPushing(true);
    try {
      const next = await apiFetch<ServiceRequest>(
        v1Path(`/projects/${projectId}/service-requests/${request.work_item.id}/push`),
        { method: 'POST', body: '{}' },
      );
      replace(next);
      toast('Project tasks created');
    } catch (err) {
      toastError(err, 'Could not push this request into projects');
    } finally {
      setPushing(false);
    }
  }

  async function patchWorkItem(fields: Record<string, unknown>) {
    const item = await apiFetch<WorkItemFull>(
      v1Path(`/projects/${projectId}/work-items/${request.work_item.id}`),
      {
        method: 'PATCH',
        body: JSON.stringify({ version: request.work_item.version, ...fields }),
      },
    );
    const next = { ...request, work_item: item };
    replace(next);
    return item;
  }

  const flaggedNotes = Object.fromEntries(
    Object.entries(sectionFlags).map(([key, note]) => [key, note.trim()]).filter(([, note]) => note),
  );

  async function decide(role: string, decision: ServiceRequestApproval['decision']) {
    if (!approvalPhase) return;
    const comments = commentFor === role ? approvalComments.trim() : '';
    if (decision === 'commented' && !comments) {
      setCommentFor(role);
      toast('Add a comment before recording it.');
      return;
    }
    setDecidingRole(role);
    try {
      const result = await apiFetch<ApprovalDecisionResponse>(
        v1Path(`/projects/${projectId}/service-requests/${request.work_item.id}/approvals`),
        {
          method: 'POST',
          body: JSON.stringify({
            phase: approvalPhase,
            role,
            decision,
            ...(comments ? { comments } : {}),
            ...((decision === 'changes_requested' || decision === 'rejected') && Object.keys(flaggedNotes).length
              ? { section_feedback: flaggedNotes }
              : {}),
            request_version: request.details.request_version,
          }),
        },
      );
      await refetch();
      setApprovalComments('');
      setCommentFor(null);
      setSectionFlags({});
      if (result.transitioned_to) toast(`Moved to ${stageLabel(result.transitioned_to)}`);
      else toast(decision === 'commented' ? 'Comment recorded' : 'Approval decision recorded');
    } catch (err) {
      toastError(err, 'Failed to record approval');
    } finally {
      setDecidingRole(null);
    }
  }

  async function updateReadiness(id: string, completed: boolean, evidence?: string) {
    if (!canEditReadiness) return;
    try {
      const next = await apiFetch<ServiceRequest>(
        v1Path(`/projects/${projectId}/service-requests/${request.work_item.id}/readiness/${id}`),
        { method: 'PATCH', body: JSON.stringify(evidence === undefined ? { completed } : { completed, evidence }) },
      );
      replace(next);
      setEvidenceFor(null);
      setEvidenceText('');
    } catch (err) {
      toastError(err, 'Failed to update readiness');
    }
  }

  async function updateValidation(id: string, completed: boolean, evidence?: string) {
    if (!canEditValidation) return;
    try {
      const next = await apiFetch<ServiceRequest>(
        v1Path(`/projects/${projectId}/service-requests/${request.work_item.id}/validation/${id}`),
        { method: 'PATCH', body: JSON.stringify(evidence === undefined ? { completed } : { completed, evidence }) },
      );
      replace(next);
      setValidationEvidenceFor(null);
      setValidationEvidenceText('');
    } catch (err) {
      toastError(err, 'Failed to update validation');
    }
  }

  function setDetail(field: string, value: unknown) {
    setDraft((current) => ({ ...current, [field]: value }));
  }

  function setTypeDataField(field: string, value: unknown) {
    setDraft((current) => {
      const nextTypeData = { ...typeDataOf(current) };
      if (value === null || value === '') delete nextTypeData[field];
      else nextTypeData[field] = value;
      const totalKey = request.request_type.field_schema?.quantity_total?.[field];
      if (totalKey && serviceRequestFieldIsQuantityList(request.request_type, field)) {
        const total = quantityRowsTotal(quantityRowsOf(nextTypeData[field]));
        if (total > 0) nextTypeData[totalKey] = total;
        else delete nextTypeData[totalKey];
      }
      return { ...current, type_data: nextTypeData };
    });
  }

  const PEOPLE_ITEMS = new Set(['account_owner_id', 'commercial_owner_id']);

  function itemAnswered(item: string): boolean {
    if (item.startsWith(DETAIL_ITEM_PREFIX)) {
      const field = item.slice(DETAIL_ITEM_PREFIX.length);
      if (field === 'requestor_id' || field === 'title' || field === 'description') return true;
      const value = draft[field];
      return value !== null && value !== undefined && value !== '';
    }
    const value = typeData[item];
    if (request.request_type.field_schema?.must_confirm?.includes(item)) return value === true;
    if (serviceRequestFieldIsQuantityList(request.request_type, item)) return quantityRowsComplete(quantityRowsOf(value));
    if (Array.isArray(value)) return value.length > 0;
    return value !== null && value !== undefined && value !== '';
  }

  function fillFromNotes(answers: NotesAnswer[]) {
    const { details, typeData: typeAnswers } = splitAnswers(answers);
    setDraft((current) => {
      const next: Record<string, unknown> = { ...current };
      for (const [field, value] of Object.entries(details)) {
        if (next[field] === null || next[field] === undefined || next[field] === '') next[field] = value;
      }
      const nextTypeData = { ...typeDataOf(current) };
      for (const [field, value] of Object.entries(typeAnswers)) {
        const existing = nextTypeData[field];
        if (existing === null || existing === undefined || existing === '') nextTypeData[field] = value;
      }
      return { ...next, type_data: nextTypeData };
    });
    toast(`Filled ${answers.length} ${answers.length === 1 ? 'answer' : 'answers'} from your notes`);
  }

  async function deleteRequest() {
    const ok = await confirm({
      title: `Delete ${request.work_item.key}?`,
      description: `"${request.work_item.title}" will be removed from Service requests for everyone. Pulse keeps the record, so it can be recovered if this was a mistake.`,
      confirmLabel: 'Delete request',
      destructive: true,
    });
    if (!ok) return;
    try {
      await deleteServiceRequest(request.work_item);
      toast(`${request.work_item.key} deleted`);
      if (onDeleted) onDeleted();
      else navigate('/service-requests');
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Failed to delete the request');
    }
  }

  function renderItem(item: string) {
    if (!item.startsWith(DETAIL_ITEM_PREFIX)) {
      return <DetailField key={item} field={item} value={typeData[item]} typeData={typeData} requestType={request.request_type} disabled={readOnly} onChange={(value) => setTypeDataField(item, value)} />;
    }
    const field = item.slice(DETAIL_ITEM_PREFIX.length);
    if (field === 'title' || field === 'description') return null;
    if (field === 'requestor_id') {
      return (
        <div key={field} className="space-y-1">
          <p className="text-xs text-muted-foreground">{serviceRequestFieldLabel(field)}</p>
          <p className="py-2 text-sm">{request.requestor?.name ?? 'Unknown'}</p>
        </div>
      );
    }
    if (PEOPLE_ITEMS.has(field)) {
      return (
        <MemberPicker
          key={field}
          id={`handover-${field}`}
          label={serviceRequestFieldLabel(field)}
          value={typeof draft[field] === 'string' ? draft[field] as string : null}
          members={memberOptions}
          fallbackName={field === 'account_owner_id' ? request.account_owner?.name : request.commercial_owner?.name}
          disabled={readOnly}
          onChange={(value) => setDetail(field, value)}
        />
      );
    }
    return <DetailField key={field} field={field} value={draft[field]} disabled={readOnly} onChange={(value) => setDetail(field, value)} />;
  }

  const initial = request.initial_approval;
  const deployment = request.deployment_approval;
  const sla = request.sla;
  /** Which approval the request is waiting on, if any; drives the single waiting card. */
  const waitingPhase: ApprovalPhase | null = (stage === 'submitted' || stage === 'under_review') && initial.status !== 'approved'
    ? 'initial'
    : stage === 'deployment_approval' && deployment.status === 'pending' ? 'deployment' : null;
  const showReadinessCard = stage === 'draft' || stage === 'changes_required';
  const hasValidationChecklist = request.validation.items.length > 0;
  const validationIncomplete = stage === 'validation' && hasValidationChecklist && request.validation.percent < 100;
  const initialApprovalOutstanding = Boolean(action?.requiresInitialApproval) && initial.status !== 'approved';
  const stageActionDisabled = transitioning
    || readOnly
    || (action?.requiresDeploymentApproval && !deploymentApproved)
    || initialApprovalOutstanding
    || validationIncomplete
    || Boolean(submitReason);
  const stateOptions = transitionStateOptions(states, request.work_item.state_id, stage);

  function stageActionButton(className?: string) {
    if (!action) return null;
    const button = (
      <Button className={className} disabled={Boolean(stageActionDisabled)} onClick={() => void runStageAction()}>
        {(action.requiresDeploymentApproval && !deploymentApproved) || initialApprovalOutstanding || validationIncomplete || submitReason ? <Lock className="h-4 w-4" aria-hidden /> : null}
        {transitioning ? 'Working…' : action.confirmsValidation && hasValidationChecklist ? 'Complete request' : action.label}
      </Button>
    );
    if (!submitReason) return button;
    return (
      <Tooltip>
        <TooltipTrigger asChild><span tabIndex={0} className="inline-flex">{button}</span></TooltipTrigger>
        <TooltipContent side="bottom" className="max-w-xs">{submitReason}</TooltipContent>
      </Tooltip>
    );
  }

  /** Roles the viewer can still decide on go in "Your decision"; everything else is context. */
  const yourRows = roleRows.filter((row) => row.actionable && row.decision?.decision !== 'approved' && !readOnly);
  const waitingSummary = waitingPhase === 'initial' ? initial : waitingPhase === 'deployment' ? deployment : null;
  const waitingRole = waitingSummary ? waitingSummary.current_role ?? waitingSummary.missing_roles[0] ?? null : null;
  const reviewing = Boolean(waitingRole && yourRows.some((row) => row.role === waitingRole));

  const saveBar = autoSave ? (
    <div className="flex items-center justify-end gap-2 text-xs text-muted-foreground" role="status" aria-live="polite">
      {autoSaveState === 'error' && !saving && failedDraft.current === draft ? (
        <>
          <CloudOff className="h-3.5 w-3.5 text-destructive" aria-hidden />
          <span className="text-destructive">Couldn't save your changes</span>
          <Button size="sm" variant="outline" onClick={() => { failedDraft.current = null; void saveDetails(true); }}>Retry</Button>
        </>
      ) : saving || dirty ? (
        <span>Saving…</span>
      ) : autoSaveState === 'saved' ? (
        <>
          <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" aria-hidden />
          <span>All changes saved</span>
        </>
      ) : (
        <span>Changes save automatically</span>
      )}
    </div>
  ) : readOnly ? null : (
    <div className={cn('flex items-center justify-end gap-3', dirty && !readOnly && 'sticky bottom-0 z-10 -mx-1 rounded-lg border bg-background/95 px-3 py-2.5 shadow-sm backdrop-blur')}>
      {dirty && !readOnly ? <span className="text-xs text-muted-foreground">Unsaved changes</span> : null}
      <Button disabled={saving || readOnly || !dirty} onClick={() => void saveDetails()}>{saving ? 'Saving…' : 'Save details'}</Button>
    </div>
  );
  const readinessPanel = (
    <div className="space-y-4">
      <Card>
        <CardContent className="pt-6">
          {!showsDeliveryReadiness(stage) ? (
            <p className="mb-3 text-sm text-muted-foreground">
              These checks start once the request is in progress. They do not affect submission or initial approval.
            </p>
          ) : null}
          <div className="flex items-center justify-between text-sm">
            <strong>Deployment Readiness: {request.readiness.percent}%</strong>
            <span>{request.readiness.completed} / {request.readiness.required} required</span>
          </div>
          <div className="mt-2 h-2 overflow-hidden rounded-full bg-muted"><div className="h-full bg-primary transition-all" style={{ width: `${request.readiness.percent}%` }} /></div>
          {(request.readiness.missing ?? []).length ? (
            <div className="mt-3 text-sm">
              <p className="font-medium">Missing:</p>
              <ul className="mt-1 list-disc space-y-0.5 pl-5 text-muted-foreground">
                {request.readiness.missing.map((label) => <li key={label}>{label}</li>)}
              </ul>
            </div>
          ) : <p className="mt-3 flex items-center gap-2 text-sm text-green-700"><CheckCircle2 className="h-4 w-4" aria-hidden /> All mandatory checks are complete.</p>}
        </CardContent>
      </Card>
      {!canEditReadiness ? (
        <p className="flex items-center gap-2 text-xs text-muted-foreground">
          <Lock className="h-3.5 w-3.5" aria-hidden />
          {readOnly ? 'This request is read-only.' : 'Readiness is updated by the assignee, owner, requestor, account owner or an approver.'}
        </p>
      ) : null}
      <div className="grid gap-2 sm:grid-cols-2">
        {request.readiness.items.map((item) => (
          <div key={item.id} className="rounded-md border p-3">
            <div className="flex items-start gap-3">
              <button type="button" disabled={!canEditReadiness} onClick={() => void updateReadiness(item.id, !item.completed)} aria-pressed={item.completed} aria-label={`${item.completed ? 'Reopen' : 'Complete'} ${item.label}`} className="shrink-0 disabled:cursor-not-allowed">
                {item.completed ? <CheckCircle2 className="h-5 w-5 text-green-600" /> : <Circle className="h-5 w-5 text-muted-foreground" />}
              </button>
              <div className="min-w-0 flex-1 text-sm">
                <p>{item.label}{item.required ? <span className="ml-1 text-destructive">*</span> : null}</p>
                {item.evidence ? <p className="mt-0.5 text-xs text-muted-foreground">Evidence: {item.evidence}</p> : null}
                {item.completed && item.completed_at ? <p className="text-xs text-muted-foreground">Completed {timestampText(item.completed_at)}{item.completed_by_id ? ` by ${personName(item.completed_by_id)}` : ''}</p> : null}
              </div>
              {canEditReadiness ? (
                <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => { setEvidenceFor(evidenceFor === item.id ? null : item.id); setEvidenceText(item.evidence ?? ''); }}>
                  {item.evidence ? 'Edit evidence' : 'Add evidence'}
                </Button>
              ) : null}
            </div>
            {canEditReadiness && evidenceFor === item.id ? (
              <form className="mt-2 flex gap-2" onSubmit={(event) => { event.preventDefault(); void updateReadiness(item.id, true, evidenceText.trim()); }}>
                <Input autoFocus className="h-8" placeholder="Evidence (link, note, reference)" value={evidenceText} onChange={(event) => setEvidenceText(event.target.value)} />
                <Button type="submit" size="sm" className="h-8">Complete</Button>
              </form>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );

  const deploymentPanel = (
    <div className="space-y-4">
      {deploymentApproved ? null : (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            {deploymentApproved ? <CheckCircle2 className="h-4 w-4 text-green-600" aria-hidden /> : <ShieldCheck className="h-4 w-4" aria-hidden />}
            {deploymentApprovalLabel(deployment.status)}
          </CardTitle>
          <CardDescription>Deployment can only be scheduled once every deployment role has approved and readiness is complete.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          {deployment.status === 'not_requested' ? (
            <p className="text-muted-foreground">Move the request to Deployment approval to start this gate.</p>
          ) : deployment.missing_roles.length ? (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-muted-foreground">Waiting on</span>
              {deployment.missing_roles.map((role) => <Badge key={role} variant="outline">{role}</Badge>)}
            </div>
          ) : (
            <p className="text-muted-foreground">All required deployment roles have responded.</p>
          )}
        </CardContent>
      </Card>
      )}
      <FieldSection
        title="Deployment schedule"
        description="Every field here is required before the request can move to Scheduled."
        fields={DEPLOYMENT_SERVICE_REQUEST_FIELDS}
        values={draft}
        disabled={readOnly}
        onChange={(field, value) => setDetail(field, field === 'device_serial_numbers' && typeof value === 'string' ? value.split(',').map((part) => part.trim()).filter(Boolean) : value)}
      />
      {saveBar}
      {action && (stage === 'deployment_approval' || stage === 'scheduled') ? (
        <Card>
          <CardContent className="flex flex-wrap items-center justify-between gap-3 pt-6">
            <p className="text-sm text-muted-foreground">
              {stage === 'scheduled' ? 'Record the deployment once installation is complete on site.' : 'Schedule the deployment once it is authorised.'}
            </p>
            {stageActionButton()}
          </CardContent>
        </Card>
      ) : null}
    </div>
  );

  const validationPanel = (
    <div className="space-y-4">
      <Card className={cn((hasValidationChecklist ? request.validation.percent === 100 : validationConfirmedAt) && 'border-green-200 dark:border-green-900')}>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            {(hasValidationChecklist ? request.validation.percent === 100 : validationConfirmedAt)
              ? <CheckCircle2 className="h-4 w-4 text-green-600" aria-hidden />
              : <Circle className="h-4 w-4 text-muted-foreground" aria-hidden />}
            {hasValidationChecklist ? `Validation ${request.validation.percent}%` : validationConfirmedAt ? 'Validation confirmed' : 'Validation not confirmed'}
          </CardTitle>
          <CardDescription>
            {hasValidationChecklist
              ? 'Tick each check once it has been confirmed after deployment. Every required check must be done before the request can be completed.'
              : 'This older request type has no validation template. Confirm the delivered request works as asked before completing it.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <dl className="grid gap-3 sm:grid-cols-3">
            <div><dt className="text-xs text-muted-foreground">Deployed on</dt><dd className="text-sm">{dateText(deploymentDate)}</dd></div>
            <div><dt className="text-xs text-muted-foreground">Validation confirmed</dt><dd className="text-sm">{hasValidationChecklist && !validationConfirmedAt ? (request.validation.percent === 100 ? 'All checks done' : '—') : timestampText(validationConfirmedAt)}</dd></div>
            <div><dt className="text-xs text-muted-foreground">Completed</dt><dd className="text-sm">{timestampText(request.details.completed_at)}{request.details.completed_by_id ? ` · ${personName(request.details.completed_by_id)}` : ''}</dd></div>
          </dl>
          {hasValidationChecklist ? (
            <>
              <div>
                <div className="flex items-center justify-between text-sm">
                  <strong>{request.validation.percent}% complete</strong>
                  <span>{request.validation.completed} / {request.validation.required} required</span>
                </div>
                <div className="mt-2 h-2 overflow-hidden rounded-full bg-muted" aria-label={`Validation ${request.validation.percent}%`}>
                  <div className="h-full bg-primary transition-all" style={{ width: `${request.validation.percent}%` }} />
                </div>
                {request.validation.missing.length > 0 ? (
                  <p className="mt-2 text-xs text-muted-foreground">Missing: {request.validation.missing.join(', ')}</p>
                ) : null}
              </div>
              {!canEditValidation && !readOnly && stage === 'validation' ? (
                <p className="flex items-center gap-2 text-xs text-muted-foreground"><Lock className="h-3.5 w-3.5" aria-hidden />You do not have permission to update validation.</p>
              ) : null}
              <div className="grid gap-2 sm:grid-cols-2">
                {request.validation.items.map((item) => (
                  <div key={item.id} className="rounded-md border p-3">
                    <div className="flex items-start gap-3">
                      <button
                        type="button"
                        disabled={!canEditValidation}
                        onClick={() => void updateValidation(item.id, !item.completed)}
                        aria-pressed={item.completed}
                        aria-label={`${item.completed ? 'Reopen' : 'Complete'} validation: ${item.label}`}
                        className="shrink-0 disabled:cursor-not-allowed"
                      >
                        {item.completed ? <CheckCircle2 className="h-5 w-5 text-green-600" /> : <Circle className="h-5 w-5 text-muted-foreground" />}
                      </button>
                      <div className="min-w-0 flex-1 text-sm">
                        <p>{item.label}{item.required ? <span className="ml-1 text-destructive">*</span> : null}</p>
                        {item.evidence ? <p className="mt-0.5 text-xs text-muted-foreground">Evidence: {item.evidence}</p> : null}
                        {item.completed_at ? <p className="text-xs text-muted-foreground">Completed {timestampText(item.completed_at)}{item.completed_by_id ? ` by ${personName(item.completed_by_id)}` : ''}</p> : null}
                      </div>
                      {canEditValidation ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2 text-xs"
                          onClick={() => {
                            setValidationEvidenceFor(validationEvidenceFor === item.id ? null : item.id);
                            setValidationEvidenceText(item.evidence ?? '');
                          }}
                        >
                          {item.evidence ? 'Edit evidence' : 'Add evidence'}
                        </Button>
                      ) : null}
                    </div>
                    {canEditValidation && validationEvidenceFor === item.id ? (
                      <form className="mt-2 flex gap-2" onSubmit={(event) => { event.preventDefault(); void updateValidation(item.id, true, validationEvidenceText.trim()); }}>
                        <Input autoFocus className="h-8" aria-label={`Evidence for ${item.label}`} placeholder="Evidence (link, note, reference)" value={validationEvidenceText} onChange={(event) => setValidationEvidenceText(event.target.value)} />
                        <Button type="submit" size="sm" className="h-8">Complete</Button>
                      </form>
                    ) : null}
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="space-y-2 rounded-md border p-3">
              <p className="text-sm font-medium">Acceptance check</p>
              <p className="text-sm text-muted-foreground">Confirm that the delivered result matches the approved request and any agreed acceptance criteria.</p>
            </div>
          )}
          {stage === 'validation' ? (
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-muted-foreground">
                {hasValidationChecklist
                  ? 'Completion records who completed the request and when.'
                  : 'Confirming records who validated the request and when, then moves it to Completed.'}
              </p>
              {stageActionButton()}
            </div>
          ) : stage === 'completed' ? null : (
            <p className="flex items-start gap-2 text-sm text-muted-foreground">
              <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
              Validation opens once the request has been deployed. The current stage is {stageLabel(stage)}.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );

  const primaryAction = action ? (
    <div className="flex flex-col items-end gap-1">
      {stageActionButton()}
      {action.requiresDeploymentApproval && !deploymentApproved ? (
        <p className="max-w-xs text-right text-xs text-muted-foreground">
          Deployment approval must be completed first
          {deployment.missing_roles.length ? ` — waiting on ${deployment.missing_roles.join(', ')}` : ''}.
        </p>
      ) : null}
      {initialApprovalOutstanding ? (
        <p className="max-w-xs text-right text-xs text-muted-foreground">
          Awaiting initial approval decisions in the Approvals tab
          {initial.missing_roles.length ? ` (${initial.missing_roles.join(', ')})` : ''}.
        </p>
      ) : null}
      {validationIncomplete ? (
        <p className="max-w-xs text-right text-xs text-muted-foreground">
          Complete all required validation checks first ({request.validation.missing.length} remaining).
        </p>
      ) : null}
      {submitReason && !simpleDraft ? <p className="max-w-xs text-right text-xs text-muted-foreground">{submitReason}</p> : null}
    </div>
  ) : null;

  return (
    <div className="space-y-5">
      {notesOpen ? (
        <FillFromNotesDialog
          open
          onOpenChange={setNotesOpen}
          type={request.request_type}
          answered={new Set(configured.flatMap((section) => section.items).filter(itemAnswered))}
          onApply={fillFromNotes}
        />
      ) : null}
      <div className="space-y-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <span className="font-mono font-semibold text-primary">{request.work_item.key}</span>
              <Badge variant="outline">{request.request_type.name}</Badge>
              {request.implementation ? (
                <Link to={`/service-requests/implementations/${request.implementation.id}`} className="text-muted-foreground hover:text-foreground">
                  {request.implementation.name}
                </Link>
              ) : null}
            </div>
            <h1 className="mt-1 text-2xl font-semibold tracking-tight">{request.work_item.title}</h1>
            {request.work_item.description && !isRichTextEmpty(request.work_item.description) ? (
              <div
                className="rich-html mt-1 max-w-3xl text-sm text-muted-foreground"
                dangerouslySetInnerHTML={{ __html: request.work_item.description }}
              />
            ) : null}
            {simpleDraft && typeof request.details.target_go_live_date === 'string' && request.details.target_go_live_date ? (
              <p className="mt-1 text-xs text-muted-foreground">Requested completion {dateText(request.details.target_go_live_date)}</p>
            ) : null}
            <div className="mt-3">
              <OwnerPicker
                label="request"
                value={request.work_item.assignee_ids}
                members={memberOptions}
                disabled={readOnly}
                onChange={(ids) => {
                  void patchWorkItem({ assignee_ids: ids }).catch((err) => toastError(err, 'Failed to update assignees'));
                }}
              />
            </div>
          </div>
          <div className="flex items-start gap-2">
            <div className="flex flex-col items-end gap-2">
              {chrome === 'peek' ? <Button asChild variant="outline" size="sm"><a href={serviceRequestPath(request.work_item)}>Open full page</a></Button> : null}
              {simpleDraft || waitingPhase ? null : primaryAction}
            </div>
            {permissions?.can_delete ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button type="button" variant="ghost" size="icon" className="h-8 w-8" aria-label={`More actions for ${request.work_item.key}`}>
                    <MoreHorizontal className="h-4 w-4" aria-hidden />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem className="text-destructive" onSelect={() => void deleteRequest()}>
                    <Trash2 className="mr-2 h-4 w-4" aria-hidden />
                    Delete request
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : null}
          </div>
          {permissions?.can_delete ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button type="button" variant="ghost" size="icon" className="h-8 w-8" aria-label={`More actions for ${request.work_item.key}`}>
                  <MoreHorizontal className="h-4 w-4" aria-hidden />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem className="text-destructive" onSelect={() => void deleteRequest()}>
                  <Trash2 className="mr-2 h-4 w-4" aria-hidden />
                  Delete request
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}
        </div>

        {simpleDraft ? null : (
        <div className="grid grid-cols-2 gap-px overflow-hidden rounded-lg border bg-border sm:grid-cols-3 lg:grid-cols-6">
          <MetaCell label="Status">
            <Select value={request.work_item.state_id} onValueChange={(value) => void transitionTo(value)} disabled={transitioning || readOnly || stateOptions.length <= 1}>
              <SelectTrigger className="h-8 border-0 bg-transparent px-0 shadow-none focus:ring-0"><SelectValue /></SelectTrigger>
              <SelectContent>{stateOptions.map((state) => <SelectItem key={state.id} value={state.id}>{state.name}</SelectItem>)}</SelectContent>
            </Select>
          </MetaCell>
          <MetaCell label="Priority">
            <Select value={(SERVICE_REQUEST_PRIORITIES as readonly string[]).includes(request.work_item.priority) ? request.work_item.priority : 'medium'} onValueChange={(value) => void patchWorkItem({ priority: value }).catch((err) => toast(err instanceof Error ? err.message : 'Failed to update priority'))} disabled={readOnly}>
              <SelectTrigger className="h-8 border-0 bg-transparent px-0 shadow-none focus:ring-0"><SelectValue /></SelectTrigger>
              <SelectContent>{SERVICE_REQUEST_PRIORITIES.map((value) => <SelectItem key={value} value={value}>{PRIORITY_LABELS[value]}</SelectItem>)}</SelectContent>
            </Select>
          </MetaCell>
          <MetaCell label="Owner">
            <span className="block truncate py-1.5">{request.owner?.name ?? 'Unassigned'}</span>
          </MetaCell>
          <MetaCell label="Target go-live">
            <span className={cn('inline-block rounded px-1 py-1.5 tabular-nums', sla?.target_overdue && 'text-red-600 dark:text-red-400')}>
              {dateText(sla?.target_deployment_date)}
              {sla?.target_days_remaining != null && !sla.target_overdue && !closedStage ? <span className="ml-1 text-xs text-muted-foreground">({sla.target_days_remaining}d)</span> : null}
            </span>
          </MetaCell>
          <MetaCell label="SLA">
            <span className={cn('inline-flex items-center gap-1 rounded-md border px-1.5 py-1 text-xs', stage === 'rejected' || stage === 'cancelled' ? 'text-muted-foreground' : slaClass(sla))} title={sla?.sla_date ? `SLA date ${dateText(sla.sla_date)}` : undefined}>
              <Timer className="h-3.5 w-3.5" aria-hidden />
              {stage === 'completed' && sla?.sla_date && typeof request.details.completed_at === 'string'
                ? (request.details.completed_at.slice(0, 10) <= sla.sla_date.slice(0, 10) ? 'Met' : 'Missed')
                : stage === 'rejected' || stage === 'cancelled' ? 'Closed' : slaSummary(sla)}
            </span>
          </MetaCell>
          <MetaCell label="Health">
            <button
              type="button"
              className={cn('inline-flex items-center gap-1 rounded-md border px-1.5 py-1 text-xs font-medium', ragClass(request.rag))}
              title={(request.rag_reasons ?? []).join('\n') || ragLabel(request.rag)}
              aria-expanded={ragOpen}
              onClick={() => setRagOpen((value) => !value)}
            >
              {ragLabel(request.rag)}
              {(request.rag_reasons ?? []).length ? <span className="opacity-70">({request.rag_reasons.length})</span> : null}
            </button>
          </MetaCell>
        </div>
        )}
        {ragOpen && (request.rag_reasons ?? []).length ? (
          <ul className="list-disc space-y-0.5 pl-5 text-xs text-muted-foreground">
            {request.rag_reasons.map((reason) => <li key={reason}>{reason}</li>)}
          </ul>
        ) : null}

        <StageProgress stage={stage} stateName={request.state_name} />
        {stateOptions.length <= 1 && !readOnly && !simpleDraft ? (
          <p className="text-xs text-muted-foreground">No further transitions from {stageLabel(stage)}.</p>
        ) : null}
      </div>

      {gateError ? (
        <div role="alert" className="flex gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          <div>
            <p>{gateError.message}</p>
            {gateError.details.length ? (
              <ul className="mt-1 list-disc space-y-0.5 pl-5">
                {gateError.details.map((detail) => <li key={detail}>{detail}</li>)}
              </ul>
            ) : null}
          </div>
        </div>
      ) : null}

      {deployment.status === 'approved' ? (
        <div role="status" className="rounded-lg border-2 border-green-500 bg-green-50 p-4 text-green-900 dark:bg-green-950 dark:text-green-100">
          <p className="flex items-center gap-2 text-lg font-bold tracking-wide"><CheckCircle2 className="h-6 w-6" aria-hidden /> APPROVED FOR DEPLOYMENT</p>
          <p className="mt-1 text-sm">Approved {timestampText(deployment.approved_at)}</p>
          {deployment.approvers.length ? (
            <ul className="mt-2 flex flex-wrap gap-2 text-sm">
              {deployment.approvers.map((approver) => (
                <li key={`${approver.role}-${approver.user_id}`} className="rounded-md bg-white/60 px-2 py-1 dark:bg-black/20">
                  <span className="font-medium">{approver.role}</span> · {personName(approver.user_id, approver.role)} · {timestampText(approver.decided_at)}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}

      {deployment.status === 'overridden' ? (
        <div role="status" className="rounded-lg border-2 border-amber-500 bg-amber-50 p-4 text-amber-900 dark:bg-amber-950 dark:text-amber-100">
          <p className="flex items-center gap-2 text-lg font-bold tracking-wide"><ShieldAlert className="h-6 w-6" aria-hidden /> APPROVED FOR DEPLOYMENT (management override)</p>
          <p className="mt-1 text-sm">
            Overridden by {personName(deployment.override?.user_id, 'an organization admin')} {timestampText(deployment.override?.decided_at)}
          </p>
          <dl className="mt-2 grid gap-2 text-sm sm:grid-cols-2">
            <div><dt className="font-medium">Reason</dt><dd>{deployment.override?.reason || '—'}</dd></div>
            <div><dt className="font-medium">Outstanding requirement</dt><dd>{deployment.override?.outstanding_requirement || '—'}</dd></div>
          </dl>
        </div>
      ) : null}

      {deployment.status === 'pending' && preDeployment && waitingPhase !== 'deployment' ? (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base"><ShieldCheck className="h-4 w-4" aria-hidden /> Deployment approval pending</CardTitle>
            <CardDescription>Is everything ready and are we authorised to deploy this?</CardDescription>
          </CardHeader>
          <CardContent className="text-sm">
            {deployment.missing_roles.length ? (
              <>
                <p className="text-muted-foreground">Waiting on:</p>
                <ul className="mt-1 flex flex-wrap gap-2">
                  {deployment.missing_roles.map((role) => <li key={role}><Badge variant="outline">{role}</Badge></li>)}
                </ul>
              </>
            ) : <p className="text-muted-foreground">All required roles have responded.</p>}
            {deployment.approvers.length ? (
              <p className="mt-2 text-xs text-muted-foreground">
                Approved so far: {deployment.approvers.map((approver) => `${approver.role} (${personName(approver.user_id, approver.role)})`).join(', ')}
              </p>
            ) : null}
          </CardContent>
        </Card>
      ) : null}

      <div className="space-y-4 pt-2">
        {stage === 'changes_required' ? (() => {
          const feedback = request.approvals
            .filter((approval) => approval.decision === 'changes_requested')
            .sort((a, b) => b.decided_at.localeCompare(a.decided_at))[0];
          if (!feedback) return null;
          return (
            <div role="status" className="flex gap-3 rounded-xl border border-amber-300 bg-amber-50 p-4 text-amber-950 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-100">
              <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" aria-hidden />
              <div className="min-w-0 space-y-1">
                <p className="text-sm font-semibold">
                  Changes requested by {feedback.role} ({personName(approvalApproverId(feedback, request), feedback.role)})
                  {Object.keys(feedback.section_feedback ?? {}).length
                    ? ` · ${Object.keys(feedback.section_feedback ?? {}).length} section${Object.keys(feedback.section_feedback ?? {}).length === 1 ? '' : 's'} to update`
                    : ''}
                </p>
                {feedback.comments ? <p className="whitespace-pre-wrap text-sm">{feedback.comments}</p> : null}
                <p className="text-xs opacity-80">{timestampText(feedback.decided_at)} · Update the answers below, save, then resubmit.</p>
              </div>
            </div>
          );
        })() : null}
        {stage === 'rejected' ? (() => {
          const rejection = request.approvals
            .filter((approval) => approval.decision === 'rejected')
            .sort((a, b) => b.decided_at.localeCompare(a.decided_at))[0];
          if (!rejection) return null;
          const sectionNotes = Object.entries(rejection.section_feedback ?? {});
          return (
            <div role="status" className="flex gap-3 rounded-xl border border-red-300 bg-red-50 p-4 text-red-950 dark:border-red-900 dark:bg-red-950 dark:text-red-100">
              <XCircle className="mt-0.5 h-5 w-5 shrink-0 text-red-600" aria-hidden />
              <div className="min-w-0 space-y-1">
                <p className="text-sm font-semibold">
                  Rejected by {rejection.role} ({personName(approvalApproverId(rejection, request), rejection.role)})
                </p>
                {rejection.comments ? <p className="whitespace-pre-wrap text-sm">{rejection.comments}</p> : null}
                {sectionNotes.length ? (
                  <ul className="space-y-0.5 text-sm">
                    {sectionNotes.map(([key, note]) => (
                      <li key={key}>
                        <span className="font-medium">{sectionProgress.find((section) => section.key === key)?.label ?? key}:</span> {note}
                      </li>
                    ))}
                  </ul>
                ) : null}
                <p className="text-xs opacity-80">{timestampText(rejection.decided_at)} · This request is closed. Raise a new request if it is still needed.</p>
              </div>
            </div>
          );
        })() : null}
        {showReadinessCard ? (
          <Card>
            <CardHeader className="flex-row items-center justify-between gap-3 space-y-0 pb-3">
              <div>
                <CardTitle className="text-base">
                  {sectionsLeft ? `${sectionsLeft} of ${sectionProgress.length} sections to finish` : 'All sections complete'}
                </CardTitle>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                {!readOnly ? (
                  <Button type="button" variant="outline" size="sm" onClick={() => setNotesOpen(true)}>
                    <ClipboardPaste className="mr-1.5 h-4 w-4" aria-hidden />
                    Fill from notes
                  </Button>
                ) : null}
                {stageActionButton()}
              </div>
            </CardHeader>
            <CardContent className="grid gap-2 sm:grid-cols-2">
              {sectionProgress.map((section, index) => {
                const flagged = Boolean(feedbackBySection[section.key]);
                const complete = section.answered >= section.required && !flagged;
                const percent = section.required ? Math.round((section.answered / section.required) * 100) : 100;
                return (
                  <button
                    key={section.key}
                    type="button"
                    onClick={() => goToSection(section.key)}
                    className="flex items-center gap-3 rounded-lg border px-3 py-2.5 text-left transition hover:border-primary/40 hover:bg-muted/40"
                  >
                    {complete
                      ? <CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-500" aria-hidden />
                      : <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[11px] font-semibold text-primary">{index + 1}</span>}
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium">{section.label}</span>
                      <span className="mt-1 block h-1 overflow-hidden rounded-full bg-muted">
                        <span className={cn('block h-full', complete ? 'bg-emerald-500' : 'bg-amber-500')} style={{ width: `${percent}%` }} />
                      </span>
                    </span>
                    <span className={cn('shrink-0 text-xs tabular-nums', flagged ? 'font-medium text-amber-700' : complete ? 'text-emerald-600' : 'text-muted-foreground')}>
                      {flagged ? 'Needs changes' : section.required ? `${section.answered}/${section.required}` : 'Optional'}
                    </span>
                  </button>
                );
              })}
            </CardContent>
          </Card>
        ) : null}
        <div className="space-y-4">
          {waitingPhase ? (() => {
            const summary = waitingPhase === 'initial' ? initial : deployment;
            const roles = request.effective_roles?.[waitingPhase]
              ?? (waitingPhase === 'initial' ? request.request_type.initial_approval_roles : request.request_type.deployment_approval_roles);
            const done = roles.filter((role) => summary.approvers.some((row) => row.role === role)).length;
            const current = summary.current_role ?? summary.missing_roles[0] ?? null;
            const yourTurn = Boolean(current && yourRows.some((row) => row.role === current));
            return (
              <Card className="border-violet-200 dark:border-violet-900">
                <CardHeader className="flex-row items-center justify-between gap-3 space-y-0 pb-3">
                  <div>
                    <CardTitle className="text-base">
                      {current ? `Waiting on ${current}` : 'All approvals in'}
                    </CardTitle>
                    <CardDescription>
                      {waitingPhase === 'initial' ? 'Initial approval' : 'Deployment approval'} · {done} of {roles.length} approved
                    </CardDescription>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  {returnedFeedback && waitingPhase === 'initial' ? (
                    <p className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-100">
                      <Flag className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
                      <span>
                        Resubmitted after {returnedFeedback.role} requested changes on {timestampText(returnedFeedback.decided_at)}.
                        {Object.keys(feedbackBySection).length
                          ? ` Check the ${Object.keys(feedbackBySection).length} flagged section${Object.keys(feedbackBySection).length === 1 ? '' : 's'} below before deciding.`
                          : ''}
                        {returnedFeedback.comments ? ` Note: “${returnedFeedback.comments}”` : ''}
                      </span>
                    </p>
                  ) : null}
                  <div className="flex gap-1" aria-hidden>
                    {roles.map((role) => (
                      <span key={role} className={cn('h-1.5 flex-1 rounded-full', summary.approvers.some((row) => row.role === role) ? 'bg-violet-500' : 'bg-muted')} />
                    ))}
                  </div>
                  <ol className="space-y-2 text-sm">
                    {roles.map((role, index) => {
                      const approval = summary.approvers.find((row) => row.role === role);
                      const isCurrent = !approval && role === current;
                      return (
                        <li
                          key={role}
                          className={cn(
                            'flex items-center gap-3 rounded-lg px-3 py-2',
                            isCurrent ? 'bg-violet-50 font-medium dark:bg-violet-950' : !approval && 'text-muted-foreground',
                          )}
                        >
                          {approval
                            ? <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-500" aria-hidden />
                            : isCurrent
                              ? <Timer className="h-4 w-4 shrink-0 text-violet-600" aria-hidden />
                              : <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full border text-[10px]">{index + 1}</span>}
                          <span className="min-w-0 flex-1">{role}</span>
                          <span className="shrink-0 text-xs text-muted-foreground">
                            {approval
                              ? `${personName(approval.user_id, role)} · ${timestampText(approval.decided_at)}`
                              : isCurrent ? 'Waiting now' : 'Next'}
                          </span>
                        </li>
                      );
                    })}
                  </ol>
                  {yourTurn && current ? (() => {
                    const note = commentFor === current ? approvalComments.trim() : '';
                    const busy = decidingRole === current;
                    const flagged = Object.keys(flaggedNotes).length;
                    const unfinishedFlags = Object.keys(sectionFlags).length - flagged;
                    return (
                      <div className="space-y-2 rounded-lg border bg-background p-3">
                        <p className="text-sm font-semibold">Your decision as {current}</p>
                        <Textarea
                          aria-label="Feedback for the requestor"
                          value={commentFor === current ? approvalComments : ''}
                          placeholder={flagged ? 'Overall note (optional)' : 'Feedback for the requestor, or flag the sections below. Required to request changes or reject.'}
                          className="min-h-20"
                          onChange={(event) => { setCommentFor(current); setApprovalComments(event.target.value); }}
                        />
                        <div className="flex flex-wrap items-center justify-end gap-2">
                          {flagged || unfinishedFlags ? (
                            <span className="mr-auto flex items-center gap-1.5 text-xs text-amber-700 dark:text-amber-300">
                              <Flag className="h-3.5 w-3.5" aria-hidden />
                              {flagged} section{flagged === 1 ? '' : 's'} flagged{unfinishedFlags ? ` · ${unfinishedFlags} without a note` : ''}
                            </span>
                          ) : (
                            <span className="mr-auto text-xs text-muted-foreground">Flag sections below to point out exactly what needs changing.</span>
                          )}
                          <Button size="sm" variant="ghost" className="text-destructive" disabled={busy || (!note && !flagged) || unfinishedFlags > 0} onClick={() => void decide(current, 'rejected')}>Reject</Button>
                          <Button size="sm" variant="secondary" disabled={busy || (!note && !flagged) || unfinishedFlags > 0} onClick={() => void decide(current, 'changes_requested')}>Request changes</Button>
                          <Button
                            size="sm"
                            disabled={busy || flagged > 0 || unfinishedFlags > 0}
                            title={flagged || unfinishedFlags ? 'Remove the section flags to approve, or request changes instead.' : undefined}
                            onClick={() => void decide(current, 'approved')}
                          >
                            {busy ? 'Saving…' : 'Approve'}
                          </Button>
                        </div>
                      </div>
                    );
                  })() : null}
                </CardContent>
              </Card>
            );
          })() : null}
          {routesBeforeApproval ? (
          <DepartmentSteps
            requestType={request.request_type}
            details={draft}
            readOnly={readOnly}
            saving={saving}
            onSave={saveExtraSteps}
          />
          ) : null}
          {pushedTaskIds.length ? (
              <DeliveryPanel
                requestProjectId={projectId}
                requestId={request.work_item.id}
                taskIds={pushedTaskIds}
                handoffs={handoffs.map((handoff) => ({
                  projectId: handoff.linked_project_id,
                  readyStateId: handoff.ready_state_id ?? null,
                }))}
              />
            ) : canPush ? (
              <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border bg-card px-4 py-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium">Project work can start now</p>
                  <p className="text-sm text-muted-foreground">Empty answers stay on this request. You do not have to finish the form first.</p>
                </div>
                <Button type="button" disabled={pushing} onClick={() => void pushIntoProjects()}>
                  {pushing ? 'Pushing…' : 'Push into projects'}
                </Button>
              </div>
            ) : null}
          {(['approved', 'in_progress', 'ready_for_deployment'] as string[]).includes(stage) ? (
            <section className="space-y-3">
              <h2 className="text-base font-semibold">Deployment readiness</h2>
              {readinessPanel}
            </section>
          ) : null}
          {(['scheduled', 'deployed'] as string[]).includes(stage) || (stage === 'deployment_approval' && !waitingPhase) ? (
            <section className="space-y-3">
              <h2 className="text-base font-semibold">Deployment</h2>
              {deploymentPanel}
            </section>
          ) : null}
          {(['validation', 'completed'] as string[]).includes(stage) ? (
            <section className="space-y-3">
              <h2 className="text-base font-semibold">Validation</h2>
              {validationPanel}
            </section>
          ) : null}
          <div className="space-y-2">
            <AttachmentList
              projectId={projectId}
              workItemId={request.work_item.id}
              readOnly={readOnly}
              title="Documents"
              requiredDocuments={request.request_type.required_attachments ?? []}
              documentGuidance={request.request_type.required_attachment_guidance ?? {}}
              onChanged={() => void refetch()}
            />
            <p className="text-xs text-muted-foreground">Upload the product list, quotes, site photos, and anything else that belongs with this request.</p>
          </div>
          {!simpleDraft ? <h2 className="pt-2 text-base font-semibold">Requirements</h2> : null}
          {sectionProgress.map((section, index) => {
            const complete = section.answered >= section.required;
            return (
              <ConfiguredSectionCard
                key={section.key}
                id={`sr-section-${section.key}`}
                index={index}
                label={section.label}
                answered={section.answered}
                total={section.required}
                open={sectionOpen(section.key, complete)}
                onToggle={() => setOpenSections((current) => ({ ...current, [section.key]: !sectionOpen(section.key, complete) }))}
                review={reviewing ? {
                  flagged: section.key in sectionFlags,
                  note: sectionFlags[section.key] ?? '',
                  onFlag: (flag) => {
                    setSectionFlags((current) => {
                      const next = { ...current };
                      if (flag) next[section.key] = next[section.key] ?? '';
                      else delete next[section.key];
                      return next;
                    });
                  },
                  onNote: (value) => setSectionFlags((current) => ({ ...current, [section.key]: value })),
                } : null}
                feedback={feedbackBySection[section.key] && returnedFeedback
                  ? {
                    note: feedbackBySection[section.key],
                    from: `${returnedFeedback.role} · ${personName(approvalApproverId(returnedFeedback, request), returnedFeedback.role)}`,
                    label: stage === 'changes_required' ? 'Needs changes' : 'Flagged last round',
                  }
                  : null}
                byline={sectionAuthors[section.key]
                  ? `Filled in by ${sectionAuthors[section.key].people.map((id) => personName(id)).join(', ')} · ${timestampText(sectionAuthors[section.key].last)}`
                  : null}
              >
                {section.visible.map(renderItem)}
              </ConfiguredSectionCard>
            );
          })}
          {saveBar}
          <details className="group rounded-xl border bg-card px-5 py-3">
            <summary className="flex cursor-pointer list-none items-center gap-2 text-sm font-semibold">
              <ChevronDown className="h-4 w-4 -rotate-90 transition-transform group-open:rotate-0" aria-hidden />
              History
              <span className="text-xs font-normal text-muted-foreground">Every change and decision, with who and when</span>
            </summary>
            <div className="mt-3">
              <ActivityTimeline activity={activity} members={memberOptions} labels={labels} formatOptions={activityOptions} />
            </div>
          </details>
        </div>
      </div>
    </div>
  );
}

/** Falls back to the status summaries for older payloads without `approver_id`. */
function approvalApproverId(approval: ServiceRequestApproval, request: ServiceRequest): string | null {
  if (approval.approver_id) return approval.approver_id;
  if (approval.override) return request.deployment_approval.override?.user_id ?? null;
  const summary = approval.phase === 'initial' ? request.initial_approval : request.deployment_approval;
  return summary.approvers.find((row) => row.role === approval.role && row.decided_at === approval.decided_at)?.user_id
    ?? summary.approvers.find((row) => row.role === approval.role)?.user_id
    ?? null;
}
